import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import App from './App';

// 带重试机制的 lazy 加载函数
function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3,
  delay = 1000
): React.LazyExoticComponent<T> {
  return lazy(() => {
    const attemptImport = (attempt: number): Promise<{ default: T }> => {
      return importFn().catch(error => {
        if (attempt < retries) {
          console.warn(`模块加载失败，${delay}ms 后重试 (${attempt + 1}/${retries}):`, error);
          return new Promise<{ default: T }>(resolve => {
            setTimeout(() => {
              resolve(attemptImport(attempt + 1));
            }, delay);
          });
        }
        // 最后一次失败，尝试刷新页面（清除缓存）
        console.error('模块加载失败，尝试刷新页面:', error);
        if (attempt === retries && typeof window !== 'undefined') {
          // 添加时间戳避免无限刷新
          const lastReload = sessionStorage.getItem('last_module_reload');
          const now = Date.now();
          if (!lastReload || now - parseInt(lastReload) > 3000) {
            sessionStorage.setItem('last_module_reload', now.toString());
            window.location.reload();
          }
        }
        throw error;
      });
    };
    return attemptImport(0);
  });
}

// 错误边界组件
function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 p-8">
          <h1 className="text-2xl font-bold text-font-primary">{error.status === 404 ? '页面未找到' : '页面加载失败'}</h1>
          <p className="text-font-secondary">{error.status === 404 ? '抱歉，您访问的页面不存在' : error.statusText || '加载页面时发生错误'}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-primary text-font-reverse rounded-md hover:bg-primary/90">
            刷新页面
          </button>
        </div>
      </div>
    );
  }

  const errorMessage = error instanceof Error ? error.message : '未知错误';

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4 p-8">
        <h1 className="text-2xl font-bold text-font-primary">页面加载失败</h1>
        <p className="text-font-secondary">{errorMessage}</p>
        <div className="flex gap-4 justify-center">
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-primary text-font-reverse rounded-md hover:bg-primary/90">
            刷新页面
          </button>
          <button
            onClick={() => (window.location.href = '/')}
            className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90"
          >
            返回首页
          </button>
        </div>
      </div>
    </div>
  );
}

// PageContent、LivePage 和 LiveDetailPage 使用预加载，在应用启动后自动预加载
const PageContentModule = () => import('@/app/page');
const LivePageModule = () => import('@/app/live/page');
const LiveDetailPageModule = () => import('@/app/live/detail/page');

// 关键页面也使用带重试的 lazy 加载
const PageContent = lazyWithRetry(() => PageContentModule().then(m => ({ default: m.default })));
const LivePage = lazyWithRetry(() => LivePageModule().then(m => ({ default: m.default })));
const LiveDetailPage = lazyWithRetry(() => LiveDetailPageModule().then(m => ({ default: m.default })));

// 独立落地页（不继承 App 布局/全局 Context）
const LandingPage = lazyWithRetry(() => import('@/app/landing/page').then(m => ({ default: m.default })));

// 预加载状态跟踪，避免重复预加载
let criticalPagesPreloaded = false;

// 预加载函数：在用户可能访问之前预加载这些关键页面模块
export const preloadCriticalPages = () => {
  if (criticalPagesPreloaded) return;
  criticalPagesPreloaded = true;

  // 立即开始预加载（不等待空闲时间，因为这些页面对性能要求高）
  // 使用 Promise.all 并行加载三个模块
  Promise.all([
    PageContentModule().catch(err => console.warn('预加载 PageContent 失败:', err)),
    LivePageModule().catch(err => console.warn('预加载 LivePage 失败:', err)),
    LiveDetailPageModule().catch(err => console.warn('预加载 LiveDetailPage 失败:', err)),
  ]).then(() => {
    console.log('✓ PageContent、LivePage 和 LiveDetailPage 预加载完成');
  });
};

// 使用带重试机制的 lazy 加载
const LiveReservePage = lazyWithRetry(() => import('@/app/live/reserve/page').then(m => ({ default: m.default })));
const SquarePage = lazyWithRetry(() => import('@/app/components/Dynamic').then(m => ({ default: m.default })));
const MessagePage = lazyWithRetry(() => import('@/app/components/Message').then(m => ({ default: m.default })));
const PredictPage = lazyWithRetry(() => import('@/app/predict/page').then(m => ({ default: m.default })));
const PredictLayout = lazyWithRetry(() => import('@/app/predict/layout').then(m => ({ default: m.default })));
const PredictDetailPage = lazyWithRetry(() => import('@/app/predict/detail/page').then(m => ({ default: m.default })));
const PredictSettingsPage = lazyWithRetry(() => import('@/app/predict/settings/page').then(m => ({ default: m.default })));
const PredictSportPage = lazyWithRetry(() => import('@/app/predict/sport/page').then(m => ({ default: m.default })));
const PredictSportsPage = lazyWithRetry(() => import('@/app/predict/sports/page').then(m => ({ default: m.default })));
const MePage = lazyWithRetry(() => import('@/app/me/page').then(m => ({ default: m.default })));
const MePointPage = lazyWithRetry(() => import('@/app/me/point').then(m => ({ default: m.default })));
const MeFormPage = lazyWithRetry(() => import('@/app/me/form/page').then(m => ({ default: m.default })));
const LoginPage = lazyWithRetry(() => import('@/app/login/page').then(m => ({ default: m.default })));
const SearchPage = lazyWithRetry(() => import('@/app/search/page').then(m => ({ default: m.default })));
const MarketsPage = lazyWithRetry(() => import('@/app/markets/page').then(m => ({ default: m.default })));
const FeedbackPage = lazyWithRetry(() => import('@/app/feedback/page').then(m => ({ default: m.default })));
const ReferralPage = lazyWithRetry(() => import('@/app/referral/page').then(m => ({ default: m.default })));
const InvitePage = lazyWithRetry(() => import('@/app/invite/page').then(m => ({ default: m.default })));
const ChatInvitePage = lazyWithRetry(() => import('@/app/chat-invite/page').then(m => ({ default: m.default })));
const StreamPage = lazyWithRetry(() => import('@/app/stream/page').then(m => ({ default: m.default })));
const StreamPage1 = lazyWithRetry(() => import('@/app/stream/helper').then(m => ({ default: m.default })));
const StreamToolsPage = lazyWithRetry(() => import('@/app/stream/tools/page').then(m => ({ default: m.default })));
const TestIconsPage = lazyWithRetry(() => import('@/app/test-icons/page').then(m => ({ default: m.default })));
const StreamCenterPage = lazyWithRetry(() => import('@/app/stream/center/page').then(m => ({ default: m.default })));
const PortfolioPage = lazyWithRetry(() => import('@/app/portfolio/page').then(m => ({ default: m.default })));
const PerpsPage = lazyWithRetry(() => import('@/app/perps/page').then(m => ({ default: m.default })));
const LinePage = lazyWithRetry(() => import('@/app/line/page').then(m => ({ default: m.default })));
const AnnouncementPage = lazyWithRetry(() => import('@/app/announcement/page').then(m => ({ default: m.default })));
const AnnouncementDetailPage = lazyWithRetry(() => import('@/app/announcement/detail/page').then(m => ({ default: m.default })));
const ArticlePage = lazyWithRetry(() => import('@/app/article/page').then(m => ({ default: m.default })));
const ArticleDetailPage = lazyWithRetry(() => import('@/app/article/detail/page').then(m => ({ default: m.default })));
const ProfileDetailPage = lazyWithRetry(() => import('@/app/profile/detail/page').then(m => ({ default: m.default })));
const ScanPage = lazyWithRetry(() => import('@/app/scan/page').then(m => ({ default: m.default })));
const AppDownloadPage = lazyWithRetry(() => import('@/app/app-download/page').then(m => ({ default: m.default })));
const AiPage = lazyWithRetry(() => import('@/app/ai/page').then(m => ({ default: m.default })));
const SwapPage = lazyWithRetry(() => import('@/app/swap/page').then(m => ({ default: m.default })));
const TwitterCallbackPage = lazyWithRetry(() => import('@/app/twitter-callback/page').then(m => ({ default: m.default })));
const PrivacyPage = lazyWithRetry(() => import('@/app/help/privacy').then(m => ({ default: m.default })));
const SupportPage = lazyWithRetry(() => import('@/app/help/support').then(m => ({ default: m.default })));
const TermsPage = lazyWithRetry(() => import('@/app/help/terms').then(m => ({ default: m.default })));
const CreatorWeeklyPage = lazyWithRetry(() => import('@/app/creator-weekly/page').then(m => ({ default: m.default })));
const ExamPage = lazyWithRetry(() => import('@/app/exam/page').then(m => ({ default: m.default })));
// /detail?id=xxx → /square/detail/xxx 重定向
function DetailRedirect() {
  const id = new URLSearchParams(window.location.search).get('id') || '';
  return <Navigate to={id ? `/square/detail/${id}` : '/square'} replace />;
}

function PageFallback() {
  return <div className="min-h-screen flex items-center justify-center text-font-primary"></div>;
}

export const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <App />,
      errorElement: <ErrorBoundary />,
      children: [
        {
          index: true,
          element: <Navigate to="/discover" replace />,
        },
        // 独立落地页（共享 App provider 树，header/sidebar/footer 由 getLayoutProps 隐藏）
        {
          path: 'home',
          element: (
            <Suspense fallback={<PageFallback />}>
              <LandingPage />
            </Suspense>
          ),
        },
        // 发现
        {
          path: 'discover',
          element: (
            <Suspense fallback={<PageFallback />}>
              <PageContent />
            </Suspense>
          ),
        },
        // 直播
        {
          path: 'live',
          element: (
            <Suspense fallback={<PageFallback />}>
              <LivePage />
            </Suspense>
          ),
        },
        // 主播-详情页
        {
          path: 'live/detail',
          element: (
            <Suspense fallback={<PageFallback />}>
              <LiveDetailPage />
            </Suspense>
          ),
        },
        // 主播-排行榜
        {
          path: 'live/rank',
          element: (
            <Suspense fallback={<PageFallback />}>
              <LivePage />
            </Suspense>
          ),
        },
        // /detail?id=xxx → /square/detail/xxx
        {
          path: 'detail',
          element: <DetailRedirect />,
        },
        // 广场
        {
          path: 'square',
          element: (
            <Suspense fallback={<PageFallback />}>
              <SquarePage />
            </Suspense>
          ),
          children: [
            {
              path: 'detail',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <SquarePage />
                </Suspense>
              ),
            },
            {
              path: 'detail/:id',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <SquarePage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: 'live/reserve',
          element: (
            <Suspense fallback={<PageFallback />}>
              <LiveReservePage />
            </Suspense>
          ),
        },
        {
          path: 'predict',
          element: (
            <Suspense fallback={<PageFallback />}>
              <PredictLayout>
                <Outlet />
              </PredictLayout>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageFallback />}>
                  <PredictPage />
                </Suspense>
              ),
            },
            {
              path: 'detail',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <PredictDetailPage />
                </Suspense>
              ),
            },
            {
              path: 'event/:slug',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <PredictDetailPage />
                </Suspense>
              ),
            },
            {
              path: 'settings',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <PredictSettingsPage />
                </Suspense>
              ),
            },
            {
              path: 'sport',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <PredictSportPage />
                </Suspense>
              ),
            },
            {
              path: 'sport/:slug',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <PredictSportPage />
                </Suspense>
              ),
            },
            {
              path: 'sports',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <PredictSportsPage />
                </Suspense>
              ),
            },
            {
              path: ':slug',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <PredictPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: 'me/form',
          element: (
            <Suspense fallback={<PageFallback />}>
              <MeFormPage />
            </Suspense>
          ),
        },
        {
          path: 'message',
          element: (
            <Suspense fallback={<PageFallback />}>
              <MessagePage />
            </Suspense>
          ),
          children: [
            { index: true, element: null },
            { path: 'public-groups', element: null },
            { path: 'contacts', element: null },
            { path: 'approvals', element: null },
            { path: 'dm/:userId', element: null },
            { path: 'group/:groupId', element: null },
          ],
        },
        {
          path: 'login',
          element: (
            <Suspense fallback={<PageFallback />}>
              <LoginPage />
            </Suspense>
          ),
        },
        {
          path: 'search',
          element: (
            <Suspense fallback={<PageFallback />}>
              <SearchPage />
            </Suspense>
          ),
        },
        {
          path: 'markets',
          element: (
            <Suspense fallback={<PageFallback />}>
              <MarketsPage />
            </Suspense>
          ),
        },
        {
          path: 'feedback',
          element: (
            <Suspense fallback={<PageFallback />}>
              <FeedbackPage />
            </Suspense>
          ),
        },
        {
          path: 'referral',
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageFallback />}>
                  <ReferralPage />
                </Suspense>
              ),
            },
            {
              path: ':type',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <ReferralPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: 'invite',
          element: (
            <Suspense fallback={<PageFallback />}>
              <InvitePage />
            </Suspense>
          ),
        },
        {
          path: 'invite/:code',
          element: (
            <Suspense fallback={<PageFallback />}>
              <InvitePage />
            </Suspense>
          ),
        },
        {
          path: 'chat-invite',
          element: (
            <Suspense fallback={<PageFallback />}>
              <ChatInvitePage />
            </Suspense>
          ),
        },
        {
          path: 'stream',
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageFallback />}>
                  <StreamPage />
                </Suspense>
              ),
            },
            {
              path: ':type',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <StreamPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: 'livecenter',
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageFallback />}>
                  <StreamCenterPage />
                </Suspense>
              ),
            },
            {
              path: ':section',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <StreamCenterPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: 'stream/helper',
          element: (
            <Suspense fallback={<PageFallback />}>
              <StreamPage1 />
            </Suspense>
          ),
        },
        {
          path: 'stream/tools',
          element: (
            <Suspense fallback={<PageFallback />}>
              <StreamToolsPage />
            </Suspense>
          ),
        },
        {
          path: 'test-icons',
          element: (
            <Suspense fallback={<PageFallback />}>
              <TestIconsPage />
            </Suspense>
          ),
        },
        {
          path: 'portfolio',
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageFallback />}>
                  <PortfolioPage />
                </Suspense>
              ),
            },
            {
              path: ':accountType',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <PortfolioPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: 'perps',
          element: (
            <Suspense fallback={<PageFallback />}>
              <PerpsPage />
            </Suspense>
          ),
        },
        {
          path: 'line',
          element: (
            <Suspense fallback={<PageFallback />}>
              <LinePage />
            </Suspense>
          ),
        },
        // 下线公告列表
        {
          path: 'announcement',
          element: (
            <Suspense fallback={<PageFallback />}>
              <AnnouncementPage />
            </Suspense>
          ),
        },
        {
          path: 'announcement/detail',
          element: (
            <Suspense fallback={<PageFallback />}>
              <AnnouncementDetailPage />
            </Suspense>
          ),
        },
        {
          path: 'article',
          element: (
            <Suspense fallback={<PageFallback />}>
              <ArticlePage />
            </Suspense>
          ),
        },
        {
          path: 'article/detail',
          element: (
            <Suspense fallback={<PageFallback />}>
              <ArticleDetailPage />
            </Suspense>
          ),
        },
        {
          path: 'profile/detail',
          element: (
            <Suspense fallback={<PageFallback />}>
              <ProfileDetailPage />
            </Suspense>
          ),
        },
        {
          path: 'scan',
          element: (
            <Suspense fallback={<PageFallback />}>
              <ScanPage />
            </Suspense>
          ),
        },
        {
          path: 'app-download',
          element: (
            <Suspense fallback={<PageFallback />}>
              <AppDownloadPage />
            </Suspense>
          ),
        },
        {
          path: 'ai',
          element: (
            <Suspense fallback={<PageFallback />}>
              <AiPage />
            </Suspense>
          ),
        },
        {
          path: 'swap',
          element: (
            <Suspense fallback={<PageFallback />}>
              <SwapPage />
            </Suspense>
          ),
        },
        {
          path: 'twitter-callback',
          element: (
            <Suspense fallback={<PageFallback />}>
              <TwitterCallbackPage />
            </Suspense>
          ),
        },
        {
          path: 'help/privacy',
          element: (
            <Suspense fallback={<PageFallback />}>
              <PrivacyPage />
            </Suspense>
          ),
        },
        {
          path: 'help/support',
          element: (
            <Suspense fallback={<PageFallback />}>
              <SupportPage />
            </Suspense>
          ),
        },
        {
          path: 'help/terms',
          element: (
            <Suspense fallback={<PageFallback />}>
              <TermsPage />
            </Suspense>
          ),
        },
        {
          path: 'creator-weekly',
          element: (
            <Suspense fallback={<PageFallback />}>
              <CreatorWeeklyPage />
            </Suspense>
          ),
        },
        // 考试
        {
          path: 'exam/:id',
          element: (
            <Suspense fallback={<PageFallback />}>
              <ExamPage />
            </Suspense>
          ),
        },
        // 用户个人页
        {
          path: ':username',
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageFallback />}>
                  <MePage />
                </Suspense>
              ),
            },
            {
              path: ':tab',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <MePage />
                </Suspense>
              ),
            },
            {
              path: ':tab/:type',
              element: (
                <Suspense fallback={<PageFallback />}>
                  <MePage />
                </Suspense>
              ),
            },
          ],
        },
        { path: '*', element: <Navigate to="/" replace /> },
      ],
    },
  ],
  { basename: '/' }
);
